import { Injectable } from '@angular/core';
import {
  TixGetReservationTicketsZipGQL,
  TixGetTicketIdsInReservationGQL,
  TixGetTicketPdfUrlsGQL
} from '@tix/data-access';
import { environment } from 'apps/purchasing-portal/src/environments/environment';
import { LoggerService } from 'apps/purchasing-portal/src/app/logger.service';

const BASE_TICKET_QR_CODE_SCAN_URL = window.location.origin + '/check-ticket';

@Injectable({
  providedIn: 'root'
})
export class TicketDownloadService {
  constructor(
    private getTicketPdfUrlsQuery: TixGetTicketPdfUrlsGQL,
    private getReservationTicketsZipQuery: TixGetReservationTicketsZipGQL,
    private getReservationTicketIdsQuery: TixGetTicketIdsInReservationGQL,
    private logger: LoggerService
  ) {}

  private async getReservationTicketIds(
    reservationId: string
  ): Promise<string[]> {
    try {
      const res = await this.getReservationTicketIdsQuery
        .fetch({ reservationId })
        .toPromise();

      const reservation = res.data.Reservation[0];

      if (!reservation) {
        this.logger.error({
          message: 'Reservation not found when fetching ticket IDs',
          event: 'reservation_tickets_fetch_failure',
          data: { reservationId }
        });
        throw new Error('Reservation not found');
      }

      const ticketIds = reservation.tickets.map(ticket => ticket.ticketId);
      this.logger.info({
        message: 'Successfully fetched reservation ticket IDs',
        event: 'reservation_tickets_fetch_success',
        data: { reservationId, ticketCount: ticketIds.length }
      });

      return ticketIds;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.logger.error({
        message: 'Failed to fetch reservation ticket IDs',
        event: 'reservation_tickets_fetch_error',
        data: { reservationId, error: errorMessage }
      });
      throw error;
    }
  }

  private async getTicketsPdfUrls(ticketIds: string[]): Promise<string[]> {
    const res = await this.getTicketPdfUrlsQuery
      .fetch({
        ticketIds,
        baseTicketQrCodeScanUrl: BASE_TICKET_QR_CODE_SCAN_URL
      })
      .toPromise();

    return res.data.getTicketPDFUrls.map(
      route => environment.customApiUrl + route
    );
  }

  public async getTicketPdfUrl(ticketId: string): Promise<string> {
    try {
      const urls = await this.getTicketsPdfUrls([ticketId]);
      this.logger.info({
        message: 'Successfully generated ticket PDF URL',
        event: 'ticket_pdf_url_generate_success',
        data: { ticketId }
      });
      return urls[0];
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.logger.error({
        message: 'Failed to generate ticket PDF URL',
        event: 'ticket_pdf_url_generate_error',
        data: { ticketId, error: errorMessage }
      });
      throw error;
    }
  }

  public async getReservationPdfUrls(reservationId: string): Promise<string[]> {
    try {
      const ticketIds = await this.getReservationTicketIds(reservationId);
      const urls = await this.getTicketsPdfUrls(ticketIds);
      this.logger.info({
        message: 'Successfully generated reservation PDF URLs',
        event: 'reservation_pdf_urls_generate_success',
        data: { reservationId, ticketCount: ticketIds.length }
      });
      return urls;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.logger.error({
        message: 'Failed to generate reservation PDF URLs',
        event: 'reservation_pdf_urls_generate_error',
        data: { reservationId, error: errorMessage }
      });
      throw error;
    }
  }

  public async getReservationZipUrl(reservationId: string): Promise<string> {
    try {
      const zipName = reservationId;

      const res = await this.getReservationTicketsZipQuery
        .fetch({
          reservationId,
          baseTicketQrCodeScanUrl: BASE_TICKET_QR_CODE_SCAN_URL,
          zipName
        })
        .toPromise();

      this.logger.info({
        message: 'Successfully generated reservation ZIP URL',
        event: 'reservation_zip_url_generate_success',
        data: { reservationId, zipName }
      });
      return environment.customApiUrl + res.data.getReservationTicketsZip;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.logger.error({
        message: 'Failed to generate reservation ZIP URL',
        event: 'reservation_zip_url_generate_error',
        data: { reservationId, error: errorMessage }
      });
      throw error;
    }
  }
}
